import React, {useEffect} from "react";
import { Button, Input, FormGroup, Intent } from "@careevolution/design-system";
import LocalizedText from "../presentational/LocalizedText";
import useTranslate from "../../hooks/useTranslate";
import { isValidVerificationCode } from './utils'
import { useResendCodeTimer } from "./hooks";

interface ForgotPasswordProps {
  emailOrPhone: string;
  verificationCode: string;
  serverError: string;
  isLoading: boolean;
  resendWait: number;
  resendPasswordResetCode: () => Promise<void>;
  setVerificationCode: (verificationCode: string) => void;
  verifyPhoneForPasswordReset: (verificationCode: string) => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  emailOrPhone,
  verificationCode,
  serverError,
  isLoading,
  resendWait,
  resendPasswordResetCode,
  setVerificationCode,
  verifyPhoneForPasswordReset,
}) => {
  const {
    onResendCode,
    waitCountdown,
    isCodeResent,
    isResendCodeReady,
  } = useResendCodeTimer(resendPasswordResetCode, resendWait)

  const translations = useTranslate(["participant-login-verification-code-label", "participant-login-next-label"])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    verifyPhoneForPasswordReset(verificationCode)
    return false
  }

  useEffect(() => {
    if (window.renderTurnstile) {
      window.renderTurnstile();
    }
  }, []);

  return (
    <form className="login-action" onSubmit={handleSubmit}>
      <h1>
        <LocalizedText textKey="participant-login-forgot-password-title" />
      </h1>
      <p>
        <LocalizedText textKey="participant-login-forgot-password-info" values={{ emailOrPhone }} />
      </p>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <FormGroup label={translations["participant-login-verification-code-label"]} className="login-field">
        <Input
          autoComplete="one-time-code"
          className="autofocus"
          disabled={isLoading}
          value={verificationCode || ''}
          onChange={(e) => setVerificationCode(e.target.value)}
          id="verificationCode"
          type="tel"
          placeholder={"123456"}
          fullWidth
          htmlAttributes={{
            pattern: "[0-9]{6}",
            maxLength: 6,
            spellCheck: false,
            autoCorrect: "off",
            autoCapitalize: "none",
          }}
        />
      </FormGroup>
      <div className="turnstile-container">
        <div id="cf-turnstile" className="cf-turnstile"></div>
      </div>
      <Button
        onClick={handleSubmit}
        disabled={!verificationCode || !isValidVerificationCode(verificationCode)}
        className="primary-action"
        width="100%"
        loading={isLoading}
        intent={Intent.primary}
        type="submit"
      >
        <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
      </Button>
      <div aria-live="polite">
        {isResendCodeReady && (
          <p className="sub-action">
            <Button onClick={onResendCode} intent={Intent.primary} disabled={isLoading}>
              <LocalizedText textKey="participant-login-resend-code-label" />
            </Button>
          </p>
        )}
        {
          isCodeResent && !isResendCodeReady && (
            <p className="sub-action">
              <LocalizedText textKey="participant-login-verify-code-resent" />
            </p>
          )
        }
        {
          !isResendCodeReady && (
            <p className="sub-action">
              <LocalizedText textKey="participant-login-verify-resend-available-format" values={{ resendWait: waitCountdown }} />
            </p>
          )
        }
      </div>
    </form>
  )
}

export default ForgotPassword